import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { rootApi } from './RootScurityClient';
import '../module_mechanics/Styles/RootDashboardActivity.css';

const FEED_POLL_MS = 5000;

function formatTime(value) {
  if (!value) return '—';
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export default function RootDashboardActivity() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState({ login_attempts: [], update_events: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await rootApi('root_dashboard_feed.php?limit=20');
      setFeed({
        login_attempts: data.login_attempts || [],
        update_events: data.update_events || [],
      });
      setError('');
    } catch (requestError) {
      if (![401, 403].includes(requestError?.status)) setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, FEED_POLL_MS);
    window.addEventListener('ourzest:data-refresh', load);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('ourzest:data-refresh', load);
    };
  }, [load]);

  const attentionCount = useMemo(() => (
    feed.update_events.filter((item) => item.delivery_status !== 'acknowledged').length
    + feed.login_attempts.filter((item) => item.login_status === 'failed').slice(0, 5).length
  ), [feed]);

  return (
    <div className="root-dashboard-activity">
      <button className="root-activity-trigger" type="button" onClick={() => setOpen((value) => !value)}>
        <span aria-hidden="true">🔔</span>
        <span>Security & updates</span>
        {attentionCount > 0 && <b>{attentionCount}</b>}
      </button>

      {open && (
        <aside className="root-activity-drawer" aria-label="Security and update activity">
          <header>
            <div><strong>Live activity</strong><small>Refreshes every 5 seconds</small></div>
            <div>
              <button type="button" onClick={load} disabled={loading} aria-label="Refresh">↻</button>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close">×</button>
            </div>
          </header>
          {error && <div className="root-activity-error">{error}</div>}

          <section>
            <h3>Update delivery</h3>
            {feed.update_events.length === 0 && <p className="root-activity-empty">No update pushes yet.</p>}
            {feed.update_events.map((item) => (
              <article key={`update-${item.target_id}`}>
                <span className={`root-activity-dot ${item.priority || 'medium'}`} />
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.username ? `${item.username} · ` : ''}{item.update_type} · {item.delivery_status}</p>
                  <time>{formatTime(item.acknowledged_at || item.delivered_at || item.published_at)}</time>
                </div>
              </article>
            ))}
          </section>

          <section>
            <h3>Login attempts</h3>
            {feed.login_attempts.length === 0 && <p className="root-activity-empty">No login attempts recorded.</p>}
            {feed.login_attempts.map((item) => (
              <article key={`login-${item.id}`}>
                <span className={`root-activity-dot ${item.login_status === 'success' ? 'success' : 'failed'}`} />
                <div>
                  <strong>{item.fullname || item.username || 'Unknown user'}</strong>
                  <p>{item.login_status} · {item.ip_address || 'unknown IP'} · {item.device_type || item.browser_name || 'device'}</p>
                  <time>{formatTime(item.login_time)}</time>
                </div>
              </article>
            ))}
          </section>
        </aside>
      )}
    </div>
  );
}
